'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';

import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';

import { useRedesSociales } from '@/components/hooks/useRedesSociales';
import type { RedEmpresa } from '@/components/hooks/useRedesSociales';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

interface AddRedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  id_empresa: string;
  redToEdit: RedEmpresa | null;
  onSaved: (red: RedEmpresa) => void;
}

export default function AddRedDialog({
  open,
  onOpenChange,
  id_empresa,
  redToEdit,
  onSaved,
}: AddRedDialogProps) {
  const { t } = useTranslation();

  const {
    tiposRed,
    idTipoRed,
    setIdTipoRed,
    url,
    setUrl,
    loading,
    saveRed,
    loadForEdit,
    resetForm,
  } = useRedesSociales(id_empresa, open);

  const modoEdicion = Boolean(redToEdit);

  useEffect(() => {
    if (open && redToEdit) {
      loadForEdit(redToEdit);
    }

    if (open && !redToEdit) {
      resetForm();
    }
  }, [open, redToEdit]);

  const handleSubmit = async () => {
    const red = await saveRed();
    if (!red) return;

    onSaved(red);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        {/* Header con X */}
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              {modoEdicion
                ? t('addRedDialog.titleEdit')
                : t('addRedDialog.titleAdd')}
            </DialogTitle>

            <DialogDescription className="text-xs text-gray-500 dark:text-gray-400">
              {modoEdicion
                ? t('addRedDialog.descEdit')
                : t('addRedDialog.descAdd')}
            </DialogDescription>
          </DialogHeader>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
          <div className="space-y-6 border border-gray-200 dark:border-white/10 rounded-xl p-4 shadow-sm bg-white dark:bg-white/5">
            {/* Tipo de red */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('addRedDialog.typeLabel')}
              </label>

              <Select
                value={idTipoRed}
                onValueChange={setIdTipoRed}
                disabled={modoEdicion}
              >
                <SelectTrigger className="rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white">
                  <SelectValue
                    placeholder={
                      modoEdicion
                        ? redToEdit?.nombre
                        : t('addRedDialog.typePlaceholder')
                    }
                  />
                </SelectTrigger>

                <SelectContent className="z-[70]">
                  {tiposRed.map((tipo) => (
                    <SelectItem key={tipo.id_tipo_red} value={tipo.id_tipo_red}>
                      {tipo.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* URL */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('addRedDialog.urlLabel')}
              </label>
              <Input
                type="url"
                placeholder="https://..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="rounded-md border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            {t('addRedDialog.cancel')}
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {loading
              ? t('addRedDialog.saving')
              : modoEdicion
              ? t('addRedDialog.update')
              : t('addRedDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}